import React, {useEffect, useState} from 'react';
import Alert from '../../Alert';
import ProductMediaPicker from './ProductMediaPicker';
import ProductGalleryMultiUploaderInner from './ProductGalleryMultiUploaderInner';
import ProductGalleryEditor from './ProductGalleryEditor';
import DigitalFileSetEditor from './DigitalFileSetEditor';
import useDebouncedAutosave from './useDebouncedAutosave';
import {supportsFileBundle} from '../../../../../shared/digitalFulfillment.js';

// Product types whose customers receive a downloadable deliverable file. The
// classic (uiVersion 1) tools set this on the Basics form; the React tools set
// it here so ebook/song/album (which have no Stock pane) and simple digital
// products can still attach their deliverable. Variants may override it with
// their own file, falling back to this product-level "main file".
const DELIVERABLE_TYPES = {
    digital: {
        label: 'Digital File',
        accept: '',
        help: 'The private video or file delivered to the customer once payment goes through. '
            + 'Products with variants can override this per variant.',
    },
    ebook: {
        label: 'EPUB E-Book File',
        accept: 'application/epub+zip',
        help: 'The EPUB file delivered to the customer once payment goes through.',
    },
    song: {
        label: 'Audio File (MP3, WAV, OGG)',
        accept: '',
        help: 'The audio file delivered to the customer once payment goes through.',
    },
    album: {
        label: 'Album ZIP File',
        accept: '',
        help: 'A ZIP file or folder containing the album, delivered once payment goes through.',
    },
};

export default function ProductWizardMedia({
    product,
    onNext,
    onBack,
    onSaveAndExit,
    onAutosave,
    error,
    onError,
}) {
    const [imageId, setImageId] = useState(product?.imageId || null);
    const [fileId, setFileId] = useState(product?.fileId || null);
    const [additionalFileIds, setAdditionalFileIds] = useState(
        product?.additionalFileIds || product?.metadata?.additionalFileIds || [],
    );
    const [clearAdditionalFiles, setClearAdditionalFiles] = useState(false);
    const [gallery, setGallery] = useState(product?.media || []);
    const [autosaveStatus, setAutosaveStatus] = useState('idle');
    const [mediaError, setMediaError] = useState('');

    useEffect(() => {
        setImageId(product?.imageId || null);
        setFileId(product?.fileId || null);
        setAdditionalFileIds(
            product?.additionalFileIds || product?.metadata?.additionalFileIds || [],
        );
        setClearAdditionalFiles(false);
        setGallery(product?.media || []);
    }, [product]);

    const deliverable = DELIVERABLE_TYPES[product?.type] || null;

    const markAutosavePending = () => {
        if (onAutosave) setAutosaveStatus('pending');
    };

    const handleImageChange = value => {
        setImageId(value);
        markAutosavePending();
    };

    const handlePrimaryFileChange = value => {
        setFileId(value);
        const remaining = additionalFileIds.filter(
            additionalId => String(additionalId) !== String(value),
        );
        if (!remaining.length && additionalFileIds.length) setClearAdditionalFiles(true);
        setAdditionalFileIds(remaining);
        markAutosavePending();
    };

    const handleAdditionalFilesChange = value => {
        // An empty list only clears a saved e-book set when the seller emptied it
        // (older clients send a bare `[]`, which the server ignores for e-books).
        if (!value.length && additionalFileIds.length) setClearAdditionalFiles(true);
        else if (value.length) setClearAdditionalFiles(false);
        setAdditionalFileIds(value);
        markAutosavePending();
    };

    const handleGalleryChange = nextGallery => {
        setGallery(nextGallery);
        setMediaError('');
    };

    const handleGalleryError = message => {
        setMediaError(message);
        onError?.(message);
    };

    const buildPayload = () => {
        const payload = { imageId: imageId || null };
        if (deliverable) payload.fileId = fileId || null;
        if (supportsFileBundle(product?.type)) {
            payload.additionalFileIds = additionalFileIds;
            if (product?.type === 'ebook' && clearAdditionalFiles && !additionalFileIds.length) {
                payload.clearAdditionalFiles = true;
            }
        }
        return payload;
    };
    const autosave = async payload => {
        if (!onAutosave) return;
        setAutosaveStatus('saving');
        try {
            await onAutosave(payload);
            setAutosaveStatus('saved');
        } catch (error) {
            setAutosaveStatus('error');
            setMediaError(error.message || 'Could not save media changes.');
            throw error;
        }
    };
    const {cancelAutosave} = useDebouncedAutosave(buildPayload(), autosave, {
        enabled: Boolean(product?.uuid),
    });

    const saveAndContinue = callback => {
        cancelAutosave();
        callback(buildPayload());
    };

    return (
        <div className="wizard-step">
            {(mediaError || error) && (
                <Alert type="error">{mediaError || error}</Alert>
            )}
            <section className="product-media-section product-media-section--listing">
                <p className="product-media-section-eyebrow">Listing media</p>
                <h2>Main product image</h2>
                <p>This is the primary thumbnail shoppers see in listings and product cards.</p>
                <div className="group">
                    <ProductMediaPicker
                        name="imageId"
                        value={imageId}
                        file={product?.image}
                        accept="image/*"
                        title="Choose main product image"
                        chooseLabel="Choose main image"
                        emptyLabel="No main image selected."
                        onChange={handleImageChange}
                    />
                </div>
            </section>

            {deliverable && (
                <section className="product-media-section product-media-section--delivery">
                    <p className="product-media-section-eyebrow">Customer delivery</p>
                    <h2>Primary file customers receive</h2>
                    <p className="product-media-section-callout">
                        Private after purchase — this is separate from your listing image and gallery.
                    </p>
                    <div className="group">
                        <h3>{deliverable.label}</h3>
                        <ProductMediaPicker
                            name="fileId"
                            value={fileId}
                            file={product?.file}
                            accept={deliverable.accept}
                            title={`Choose ${deliverable.label.toLowerCase()}`}
                            chooseLabel="Choose delivery file"
                            emptyLabel="No delivery file selected."
                            onChange={handlePrimaryFileChange}
                        />
                        <p className="help">{deliverable.help}</p>
                        {supportsFileBundle(product?.type) && (
                            <DigitalFileSetEditor
                                primaryFileId={fileId}
                                fileIds={additionalFileIds}
                                files={product?.additionalFiles || []}
                                missingFileCount={product?.missingAdditionalFileCount || 0}
                                onChange={handleAdditionalFilesChange}
                            />
                        )}
                    </div>
                </section>
            )}

            <section className="product-media-section product-media-section--listing">
                <p className="product-media-section-eyebrow">Listing media</p>
                <h2>Product gallery</h2>

                <ProductGalleryMultiUploaderInner
                    productId={product.uuid}
                    gallery={gallery}
                    onGalleryChange={handleGalleryChange}
                    onError={handleGalleryError}
                />

                <ProductGalleryEditor
                    productId={product.uuid}
                    media={gallery}
                    onChange={handleGalleryChange}
                    onError={handleGalleryError}
                />
            </section>

            {autosaveStatus !== 'idle' && (
                <p className={`product-media-autosave is-${autosaveStatus}`} role="status">
                    {autosaveStatus === 'pending' && 'Media changes will save automatically.'}
                    {autosaveStatus === 'saving' && 'Saving media changes…'}
                    {autosaveStatus === 'saved' && 'Media changes saved automatically.'}
                    {autosaveStatus === 'error' && 'Could not save media changes. Try Save and Edit Later.'}
                </p>
            )}

            <div className="wizard-nav">
                <button type="button" className="button" onClick={onBack}>
                    ← Back
                </button>
                <button
                    type="button"
                    className="button secondary"
                    onClick={() => saveAndContinue(onSaveAndExit)}
                >
                    Save and Edit Later
                </button>
                <button
                    type="button"
                    className="button primary"
                    onClick={() => saveAndContinue(onNext)}
                >
                    Next →
                </button>
            </div>
        </div>
    );
}
